// src/components/Dashboard/PlayerDashboard/PlayerExtras/PlayerNotifications.js
import React, { useEffect, useState, useRef } from "react";
import { apiFetch } from "../../../../apiFetch";
import { BASE_URL } from "../../../../config"; // keep BASE_URL for socket
import io from "socket.io-client";
import { useLocation } from "react-router-dom";
import "./PlayerNotifications.css";

export default function PlayerNotifications({ playerId }) {
  // ✅ Debug: confirm the ID your player UI is using (should be numeric playerId)
  console.log("🔎 PlayerNotifications playerId =", playerId, typeof playerId);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNotification, setSelectedNotification] = useState(null);

  const location = useLocation();
  const clickedFromRoute = location.state?.notification || null;
  const listRef = useRef(null);
  const socketRef = useRef(null);

  const activeNotification = selectedNotification ?? clickedFromRoute ?? null;

  const normalizeNotificationLink = (link) => {
    if (!link) return "";

    // If backend already sent a full URL, keep it
    if (/^https?:\/\//i.test(link)) return link;

    // If backend sent a relative route, attach the current frontend domain
    const origin = window.location.origin;
    return `${origin}${link.startsWith("/") ? "" : "/"}${link}`;
  };

  /* ===========================
     📡 FETCH NOTIFICATIONS
  =========================== */
  useEffect(() => {
    if (!playerId && playerId !== 0) return;

    const fetchNotifications = async () => {
      setLoading(true);
      setError("");

      try {
        const role = "player";
        const res = await apiFetch(`/api/notifications/${role}/${playerId}`);

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Fetch notifications failed: ${res.status} ${text}`);
        }

        const data = await res.json();

        const arr = Array.isArray(data)
          ? data
          : data?.items || data?.data || data?.notifications || [];

        const sorted = arr
          .slice()
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setNotifications(sorted);
      } catch (err) {
        console.error("❌ Error fetching notifications:", err);
        setError("Failed to load notifications.");
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [playerId]);

  /* ===========================
     🔔 REAL-TIME NOTIFICATIONS
  =========================== */
  useEffect(() => {
    // ✅ If playerId is missing, don't connect
    if (playerId === null || playerId === undefined) return;
    if (socketRef.current) return;

    socketRef.current = io(BASE_URL, {
      transports: ["websocket"], // ✅ optional stability; safe
    });

    // ✅ Register using playerId (backend expects playerId for player notifications)
    socketRef.current.emit("registerUser", { userId: playerId, role: "player" });

    socketRef.current.on("newNotification", (notification) => {
      // ✅ Debug: see what server is emitting
      console.log("📩 newNotification payload =", notification);

      // Defensive: support both camelCase & snake_case
      const targetId = notification.userId ?? notification.user_id;

      // ✅ IMPORTANT: if no targetId, DO NOT treat it as broadcast
      const sameUser =
        targetId !== undefined && targetId !== null
          ? String(targetId) === String(playerId)
          : false;

      const sameRole =
        !notification.role ||
        String(notification.role).toLowerCase() === "player";

      if (!sameUser || !sameRole) return;

      setNotifications((prev) => [{ ...notification, isRead: false }, ...prev]);
    });

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [playerId]);

  /* ===========================
     🎯 SEED FROM ROUTE
  =========================== */
  useEffect(() => {
    if (clickedFromRoute) {
      setSelectedNotification(clickedFromRoute);
    }
  }, [clickedFromRoute]);

  /* ===========================
     📍 SCROLL TO ACTIVE
  =========================== */
  useEffect(() => {
    if (activeNotification && listRef.current) {
      const el = document.getElementById(
        `notification-${activeNotification.id}`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [notifications, activeNotification]);

  const handleNotificationClick = async (note) => {
    setSelectedNotification(note);

    // optimistic UI update
    setNotifications((prev) =>
      prev.map((n) => (n.id === note.id ? { ...n, isRead: true } : n))
    );

    try {
      const role = "player";
      // ✅ Keep your existing endpoint shape (marks read for this player)
      await apiFetch(`/api/notifications/${role}/${playerId}/read`, {
        method: "PUT",
      });
    } catch (err) {
      console.error("❌ Failed to mark notifications as read:", err);
    }
  };

  /* ===========================
     🧱 RENDER
  =========================== */
  return (
    <div className="player-notifications">
      {/* ✅ Scoped wrapper class (avoids global .notifications-page conflicts) */}
      <div className="player-notifications-page">
        <h2>Player Notifications</h2>
        <p className="notifications-subtitle">
          (Important Notifications Will Show Here)
        </p>

        {loading && (
          <p className="notifications-status">Loading notifications...</p>
        )}

        {error && <p className="notifications-status error">{error}</p>}

        {activeNotification && (
          <div className="notification-preview">
            <div className="preview-header">
              <strong>{activeNotification.type || "Notification"}</strong>
              <span className="timestamp">
                {new Date(activeNotification.timestamp).toLocaleString()}
              </span>
            </div>

            <p>{activeNotification.message}</p>

            {activeNotification.link && (
              <a
                href={normalizeNotificationLink(activeNotification.link)}
                className="notification-link"
              >
                {activeNotification.linkLabel || "View"}
              </a>
            )}
          </div>
        )}

        {/* ✅ Scoped list class (avoids shared .inbox / .notifications-list conflicts) */}
        <ul className="player-notifications-list" ref={listRef}>
          {notifications.map((note) => {
            const isUnread = !note.isRead;
            const isSelected = activeNotification?.id === note.id;

            return (
              <li
                key={note.id}
                id={`notification-${note.id}`}
                className={`notification-row ${isUnread ? "unread" : "read"} ${isSelected ? "selected" : ""
                  }`}
                onClick={() => handleNotificationClick(note)}
              >
                <div className="row-left">
                  {isUnread && <span className="unread-dot" />}
                </div>

                <div className="row-content">
                  <div className="row-title">{note.type || "Notification"}</div>

                  <div className="row-message">{note.message}</div>

                  {note.link && (
                    <a
                      href={normalizeNotificationLink(note.link)}
                      className="notification-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {note.linkLabel || "View"}
                    </a>
                  )}
                </div>

                <div className="row-time">
                  {new Date(note.timestamp).toLocaleString()}
                </div>
              </li>
            );
          })}

          {!loading &&
            !error &&
            notifications.length === 0 &&
            !activeNotification && (
              <li className="notification-empty">No notifications yet.</li>
            )}
        </ul>
      </div>
    </div>
  );
}








